/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { DropDownProps, MenuProps } from "antd";
import { Dropdown as AntdDropdown } from "antd";
import classNames from "classnames";
import { RiArrowDownSLine } from "react-icons/ri";
import Typography from "components/OnahTypography";
import OnahInputError from "components/OnahInputError";

type Props = {
  className?: string;
  placeholderClassName?: string;
  buttonClassName?: string;
  options: Array<Record<string, string>>;
  map?: { label: string; value: string };
  placeholder?: string;
  onChange?: (value: string) => void;
  caret?: JSX.Element;
  leftItem?: JSX.Element;
  textClassName?: string;
  renderSelect?: (selectedLabel?: Record<string, string>) => JSX.Element;
  hideCaret?: boolean;
  value?: string;
  error?: string;
  wrapperClassName?: string;
} & DropDownProps;

const OnahDropdown: React.FC<Props> = ({
  className,
  buttonClassName,
  placeholderClassName,
  options,
  value,
  map = { label: "option", value: "value" },
  placeholder,
  onChange,
  caret,
  leftItem,
  textClassName,
  renderSelect,
  hideCaret,
  error,
  wrapperClassName,
  ...props
}) => {
  const items: MenuProps["items"] = options.map((option) => ({
    key: option[map.value],
    label: option[map.label],
  }));

  const [selectedValue, setSelectedValue] = useState("");

  useEffect(() => {
    if (options && options.length) {
      const foundOpt = options.find((opt) => opt[map.value] === value);
      if (foundOpt) {
        setSelectedValue(value || selectedValue);
      } else {
        setSelectedValue("");
      }
    }
  }, [value, options]);

  const getLabel = useMemo(() => {
    return options.find((opt) => opt[map.value] === selectedValue);
  }, [selectedValue]);

  const onSelected = (value: string) => {
    setSelectedValue(value);
    onChange && onChange(value);
  };

  const onRenderSelect = (selectedLabel?: Record<string, string>) => {
    if (renderSelect) {
      return renderSelect(selectedLabel);
    } else {
      return (
        <Typography
          type="span"
          className={classNames(
            textClassName,
            "whitespace-nowrap overflow-hidden flex-1 text-left"
          )}
        >
          {selectedLabel ? (
            selectedLabel[map.label]
          ) : (
            <span
              className={classNames(
                placeholderClassName,
                textClassName,
                "flex-1 opacity-80 whitespace-nowrap overflow-hidden"
              )}
            >
              {placeholder ?? "Select..."}
            </span>
          )}
        </Typography>
      );
    }
  };

  const renderCaret = () => {
    if (hideCaret) {
      return undefined;
    }
    return caret ?? <RiArrowDownSLine className="text-[20px]" />;
  };

  return (
    <div className={wrapperClassName}>
      <AntdDropdown
        {...props}
        menu={{
          items,
          onClick(item) {
            onSelected(item.key);
          },
        }}
        className={classNames(className)}
        trigger={["click"]}
      >
        <button
          className={classNames(buttonClassName, "justify-center")}
          onClick={(e) => e.preventDefault()}
        >
          <div className="flex items-center space-x-2">
            {leftItem}
            {onRenderSelect(getLabel)}
            {renderCaret()}
          </div>
        </button>
      </AntdDropdown>
      <div className="flex items-start">
        <OnahInputError error={error} showError={!!error} />
      </div>
    </div>
  );
};

export type IOnahDropdown = Props;

export default OnahDropdown;
